import { useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SERVICE_PHONE_RAW = '2567463933';
const SERVICE_PHONE_DISPLAY = '(256) 746-3933';
const SERVICE_HOURS = 'Hours: 8am – 10pm daily';

const buildTemplate = ({ name, phone, amount } = {}) => {
    const cleanName = (name || '').trim();
    const last4 = (phone || '').replace(/\D/g, '').slice(-4);
    const tag = [cleanName || 'a BETohalics player', last4].filter(Boolean).join(' · ');
    const amt = (amount && Number(amount) > 0) ? `$${Number(amount).toFixed(2)}` : '$___';
    return `Hi! I'm ${tag}. Just sent ${amt} via PayPal — please credit my BETohalics wallet. Thanks!`;
};

const smsHref = (msg) =>
    `sms:+1${SERVICE_PHONE_RAW}?&body=${encodeURIComponent(msg)}`;

const isAfterHoursCT = () => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Chicago', hour: 'numeric', hour12: false,
        }).formatToParts(new Date());
        const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
        return hour < 8 || hour >= 22;
    } catch { return false; }
};

/**
 * Service Line block with a pre-filled "I just paid" template and one-tap
 * Copy + Text us buttons. The Text us link is an `sms:` deep link with the
 * template pre-loaded in the body so the player's messaging app opens with
 * the message already typed — they just fill in the amount (if not provided)
 * and hit send.
 *
 * Props:
 *   - amount?: number — if known (e.g. from the OCR'd receipt) injected into
 *                       the template instead of the `$___` placeholder.
 *   - compact?: boolean — render a tighter layout for narrow dialogs.
 */
const ServiceLineWithTemplate = ({ amount, compact = false } = {}) => {
    const { user } = useAuth();
    const [copied, setCopied] = useState(false);
    const after = isAfterHoursCT();
    const template = buildTemplate({ name: user?.name, phone: user?.phone, amount });
    const href = smsHref(template);

    const copyTemplate = async () => {
        try {
            await navigator.clipboard.writeText(template);
            setCopied(true);
            toast.success('Template copied — paste it into your text app');
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error('Couldn\u2019t copy — long-press the message instead');
        }
    };

    return (
        <div className={`${compact ? 'p-2.5' : 'p-3'} rounded-lg bg-[#FFD700]/8 border border-[#FFD700]/30`} data-testid="service-line-template">
            <div className="text-[10px] uppercase tracking-widest text-[#FFD700]/80 font-bold mb-1">
                Prefer to text? Use this pre-filled message
            </div>
            <div className="flex items-center justify-between gap-2">
                <a
                    href={href}
                    className="font-mono text-white font-black text-lg hover:text-[#FFD700] transition-colors"
                    data-testid="service-line-call"
                >
                    {SERVICE_PHONE_DISPLAY}
                </a>
                <a
                    href={href}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#FFD700]/20 border border-[#FFD700]/50 text-[#FFD700] text-[11px] font-black uppercase tracking-wider hover:bg-[#FFD700]/30 transition-colors"
                    data-testid="service-line-sms"
                >
                    <MessageSquare className="w-3.5 h-3.5" /> Text us
                </a>
            </div>

            <div className="mt-2 rounded-md bg-black/35 border border-white/10 p-2 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold flex items-center justify-between">
                    <span>Pre-filled message</span>
                    <button
                        type="button"
                        onClick={copyTemplate}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border transition-colors ${
                            copied
                                ? 'bg-[#39FF14]/15 border-[#39FF14]/50 text-[#39FF14]'
                                : 'bg-[#FFD700]/15 border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/25'
                        }`}
                        data-testid="service-line-copy-template"
                        aria-label="Copy receipt template"
                    >
                        {copied ? (
                            <><Check className="w-3 h-3" /> Copied</>
                        ) : (
                            <><Copy className="w-3 h-3" /> Copy</>
                        )}
                    </button>
                </div>
                <p
                    className="text-[11px] text-zinc-200 leading-snug font-mono select-all break-words"
                    data-testid="service-line-template-text"
                >
                    {template}
                </p>
                <p className="text-[9px] text-[#A0AAB2] leading-snug">
                    Tap <span className="text-[#FFD700] font-bold">Text us</span> to open your messages with this pre-typed{amount ? '' : ' — just fill in the amount'}.
                </p>
            </div>

            {after ? (
                <div className="text-[10px] text-amber-300 mt-1.5 leading-snug" data-testid="service-line-after-hours">
                    ⏱ <span className="font-bold">Outside hours</span> — staff replies first thing at 8am. Most overnight receipts still credit fast.
                </div>
            ) : (
                <div className="text-[10px] text-zinc-400 mt-1.5 leading-snug" data-testid="service-line-hours">
                    Text us anytime — we reply <span className="text-[#FFD700]/80 font-bold">{SERVICE_HOURS}</span>.
                </div>
            )}
        </div>
    );
};

export default ServiceLineWithTemplate;
